import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useDeleteMessageMutation } from '@/modules/messaging/mutations/useDeleteMessageMutation';
import { useSendDraftMessageMutation } from '@/modules/messaging/mutations/useSendDraftMessageMutation';
import { useUpdateMessageMutation } from '@/modules/messaging/mutations/useUpdateMessageMutation';
import type { MessageApi } from '@/modules/messaging/types';
import { ConfirmDeleteDialog } from '@/modules/ui/components/confirm-delete-dialog/ConfirmDeleteDialog';
import { ConfirmDialog } from '@/modules/ui/components/confirm-dialog/ConfirmDialog';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

import { MessageBubble } from './MessageBubble';

interface MessageListProps {
  threadId: string;
  messages: MessageApi[] | undefined;
  isLoading: boolean;
  emptyState?: string;
  onMessageUpdated?: (messageId: string) => void;
  onMessageDeleted?: (messageId: string) => void;
}

type ConfirmDialogType = 'send' | 'delete' | null;

export const MessageList = ({
  threadId,
  messages = [],
  isLoading,
  emptyState = 'No messages yet. Start a conversation!',
  onMessageUpdated,
  onMessageDeleted,
}: MessageListProps) => {
  const [confirmDialog, setConfirmDialog] = useState<{
    type: ConfirmDialogType;
    messageId: string | null;
  }>({ type: null, messageId: null });

  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');

  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const updateMessageMutation = useUpdateMessageMutation();
  const sendDraftMessageMutation = useSendDraftMessageMutation();
  const deleteMessageMutation = useDeleteMessageMutation();

  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop =
          messagesContainerRef.current.scrollHeight;
      }
    };

    scrollToBottom();
  }, [messages]);

  const handleSaveEdit = (messageId: string) => {
    if (!editingContent.trim()) return;

    updateMessageMutation.mutate(
      {
        threadId,
        messageId,
        message: {
          subject: null,
          content: editingContent,
        },
      },
      {
        onSuccess: () => {
          setEditingMessageId(null);
          setEditingContent('');
          toast.success('Message has been updated');
        },
        onError: (error) => {
          toast.error(
            `Message has not been updated: ${extractErrorMessage(error)}`,
          );
        },
        onSettled: () => {
          onMessageUpdated?.(messageId);
        },
      },
    );
  };

  const handleEditMessage = (message: MessageApi) => {
    setEditingMessageId(message.id);
    setEditingContent(message.content || '');
  };

  const handleSendMessage = (messageId: string) => {
    setConfirmDialog({ type: 'send', messageId });
  };

  const confirmSendMessage = () => {
    if (!confirmDialog.messageId) return;

    const messageId = confirmDialog.messageId;
    sendDraftMessageMutation.mutate(
      { threadId, messageId },
      {
        onError: (error) => {
          toast.error(
            `Message has not been sent: ${extractErrorMessage(error)}`,
          );
        },
        onSuccess: () => {
          toast.success('Message has been sent');
          setConfirmDialog({ type: null, messageId: null });
        },
        onSettled: () => {
          onMessageUpdated?.(messageId);
        },
      },
    );
  };

  const handleDeleteMessage = (messageId: string) => {
    setConfirmDialog({ type: 'delete', messageId });
  };

  const confirmDeleteMessage = () => {
    if (!confirmDialog.messageId) return;

    const messageId = confirmDialog.messageId;
    deleteMessageMutation.mutate(
      {
        threadId,
        messageId,
      },
      {
        onSuccess: () => {
          toast.success('Message has been deleted');
          setConfirmDialog({ type: null, messageId: null });
        },
        onError: (error) => {
          toast.error(
            `Message has not been deleted: ${extractErrorMessage(error)}`,
          );
        },
        onSettled: () => {
          onMessageDeleted?.(messageId);
        },
      },
    );
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingContent('');
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="text-muted-foreground">Loading messages...</span>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="text-muted-foreground">{emptyState}</span>
      </div>
    );
  }

  const sortedMessages = [...messages].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const isPending =
    updateMessageMutation.isPending ||
    sendDraftMessageMutation.isPending ||
    deleteMessageMutation.isPending;

  return (
    <>
      <div
        ref={messagesContainerRef}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4"
      >
        <div className="flex flex-col gap-3">
          {sortedMessages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              isPending={isPending}
              isEditing={editingMessageId === message.id}
              editingContent={editingContent}
              onSendMessage={() => handleSendMessage(message.id)}
              onEditStart={() => handleEditMessage(message)}
              onEditChange={setEditingContent}
              onEditSave={() => handleSaveEdit(message.id)}
              onEditCancel={handleCancelEdit}
              onDelete={() => handleDeleteMessage(message.id)}
            />
          ))}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDialog.type === 'send'}
        onOpenChange={(open) =>
          !open && setConfirmDialog({ type: null, messageId: null })
        }
        title="Send message?"
        description="Are you sure you want to send this message?"
        confirmText="Send"
        onConfirm={confirmSendMessage}
        onCancel={() => setConfirmDialog({ type: null, messageId: null })}
      />

      <ConfirmDeleteDialog
        open={confirmDialog.type === 'delete'}
        onOpenChange={(open) =>
          !open && setConfirmDialog({ type: null, messageId: null })
        }
        title="Delete message?"
        description="Are you sure you want to delete this message?"
        onDelete={confirmDeleteMessage}
        onCancel={() => setConfirmDialog({ type: null, messageId: null })}
      />
    </>
  );
};
